import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { getGuestToken } from '../services/guestToken';

type Language = 'en' | 'ar';

interface LanguageContextType {
  lang: Language;
  dir: 'ltr' | 'rtl';
  setLang: (lang: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | null>(null);

const API_BASE_URL = import.meta.env.VITE_API_URL || '';
const STORAGE_KEY = 'garagat_lang';

function getStoredLang(): Language {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored === 'ar' || stored === 'en') return stored;
  return 'en';
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Language>(getStoredLang);
  const [translations, setTranslations] = useState<Record<string, string>>({});

  const fetchTranslations = useCallback(async (locale: Language) => {
    try {
      // Tokenless public call — attach a guest token so the gateway accepts it.
      const response = await fetch(`${API_BASE_URL}/api/translations?lang=${locale}`, {
        headers: { 'X-Guest-Token': await getGuestToken() },
      });
      if (!response.ok) throw new Error('Failed to fetch translations');
      const data: Record<string, string> = await response.json();
      setTranslations(data);
    } catch {
      setTranslations({});
    }
  }, []);

  useEffect(() => {
    fetchTranslations(lang);
  }, [lang, fetchTranslations]);

  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
  }, [lang]);

  const setLang = useCallback((newLang: Language) => {
    localStorage.setItem(STORAGE_KEY, newLang);
    setLangState(newLang);
  }, []);

  // Returns the translation value from admin DB, or the raw key if missing
  const t = useCallback((key: string): string => {
    return translations[key] || key;
  }, [translations]);

  const dir = lang === 'ar' ? 'rtl' : 'ltr';

  return (
    <LanguageContext.Provider value={{ lang, dir, setLang, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage(): LanguageContextType {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be used within LanguageProvider');
  return ctx;
}
